import { isAutomaticLog } from "hos-tools";
import { DutyStatusLogService } from "../../../../../model/DutyStatusLogs";
import { WorkdayType } from "../../../../../store/data/types";
import { DriveStore } from "../../../../../store/reducers";
import { SecurityService } from "../../../../../utils/security";
import { TopbarTranslationsDictionary } from "./BannerTranslation";
import { getCurrentVehicle, isNoVehicle } from "../../../../../utils/device";
import { BannerType } from "@geotab/zenith";
import { getPageState } from "./Helper";
import { DisplayDetailEnum } from "./ConnectionHelper";

export interface BannerDetail {
    id: BannerId;
    title: string;
    content: string;
    actionText?: string;
    action?: () => void;
    type?: BannerType;
    icon?: React.ReactNode;
}

export enum BannerId {
    BrowserSupport = "browser-support",
    ConnectionLost = "connection-lost",
    EldMalfunction = "eld-malfunction",
    EldDiagnostic = "eld-diagnostic",
    LogUpdate = "log-update",
    UpcomingViolation = "upcoming-violation",
    PcLimitWarning = "pc-limit-warning",
    WorkdayMessage = "workday-message",
    DrivingStatus = "driving-status"
}

export const getBrowserSupportMessage = (): BannerDetail | undefined => {
    if (navigator.userAgent.match(/MSIE/i) ||   navigator.userAgent.match(/Trident/i)) {
        return {
            id: BannerId.BrowserSupport,
            title: TopbarTranslationsDictionary.BrowserNotSupportedTitle,
            type: "warning",
            content: TopbarTranslationsDictionary.BrowserNotSupportedContent
        };
    }
    return undefined;
};

export const getConnectionMessage = (store: DriveStore, showNetworkMessage: boolean, currentUser: string, openConnectionDialog: (display: DisplayDetailEnum) => void): BannerDetail | undefined => {
    let appState = store.getState();
    if (currentUser && showNetworkMessage && !appState.mobile.ioxAttached) {
        return {
            id: BannerId.ConnectionLost,
            title: TopbarTranslationsDictionary.ConnectionLost,
            type: "error",
            content: TopbarTranslationsDictionary.ConnectionNetworkLost,
            actionText: TopbarTranslationsDictionary.ConnectionAction,
            action: () => openConnectionDialog(DisplayDetailEnum.network)
        };
    } else if (currentUser && getCurrentVehicle() && appState.mobile.deviceCommunicating === false && !isNoVehicle() && !appState.mobile.ioxAttached) {
        return {
            id: BannerId.ConnectionLost,
            title: TopbarTranslationsDictionary.ConnectionLost,
            type: "error",
            content: TopbarTranslationsDictionary.ConnectionVehicleLost,
            actionText: TopbarTranslationsDictionary.ConnectionAction,
            action: () => openConnectionDialog(DisplayDetailEnum.network)
        };
    }
    return undefined;
};

export const getEldJurisdictionMessage = (store: DriveStore, navigateToMalfunction: (display: DisplayDetailEnum) => void, dismissViolation: () => void): BannerDetail[] => {
    let appState = store.getState();
    let message: BannerDetail[] = [];
    if (appState.data.ELDJurisdiction !== "EWD") {
        let isUnlocked = !getPageState(store).isLocked;
        if ((appState.mobile.eldDiagnostic || appState.mobile.diagnosticViewed === false) && appState.mobile.eldMalfunction) {
            message.push({
                id: BannerId.EldMalfunction,
                title: TopbarTranslationsDictionary.EldTitle,
                type: "error",
                content: TopbarTranslationsDictionary.EldNonCompliance,
                actionText: TopbarTranslationsDictionary.EldAction,
                action: isUnlocked ? () => navigateToMalfunction(DisplayDetailEnum.malfunctions) : undefined
            });
        } else if (appState.mobile.eldDiagnostic || appState.mobile.diagnosticViewed === false) {
             message.push({
                id: BannerId.EldDiagnostic,
                title: TopbarTranslationsDictionary.EldTitle,
                type: "warning",
                content: TopbarTranslationsDictionary.EldDiagnostics,
                actionText: TopbarTranslationsDictionary.EldAction,
                action: isUnlocked ? () => navigateToMalfunction(DisplayDetailEnum.malfunctions) : undefined
            });
        } else if (appState.mobile.eldMalfunction) {
             message.push({
                id: BannerId.EldMalfunction,
                title: TopbarTranslationsDictionary.EldTitle,
                type: "error",
                content: TopbarTranslationsDictionary.EldMalfunction,
                actionText: TopbarTranslationsDictionary.EldAction,
                action: isUnlocked ? () => navigateToMalfunction(DisplayDetailEnum.malfunctions) : undefined
            });
        }

        if (appState.mobile.newUpcomingViolation) {
            message.push({
                id: BannerId.UpcomingViolation,
                type: "error",
                title: TopbarTranslationsDictionary.EldViolationTitle,
                content: TopbarTranslationsDictionary.EldUpcomingViolation,
                actionText: TopbarTranslationsDictionary.EldAction,
                action: isUnlocked ? dismissViolation : undefined
            });
        }
    }

    return message;
};

export const getPCLimitWarningMessage = (store: DriveStore, dismissBannerMessage): BannerDetail | undefined => {
    let appState = store.getState();
    if (appState.mobile.pcLimitWarningMessage) {
        let isUnlocked = !getPageState(store).isLocked;
        return {
            id: BannerId.PcLimitWarning,
            type: "warning",
            title: TopbarTranslationsDictionary.PcLimitTitle,
            content: `${ appState.mobile.pcLimitWarningMessage }`,
            actionText: TopbarTranslationsDictionary.PcLimitAction,
            action: isUnlocked ? dismissBannerMessage : undefined
        };
    }
    return undefined;
};

export const getLogChangeMessage = (store: DriveStore, inspectLogChange: boolean, isDvirLogRemoved: boolean, openDvirPage: () => void): BannerDetail | undefined => {
    if (inspectLogChange) {
        let isUnlocked = !getPageState(store).isLocked;
        // let tapChangesMessage = isUnlocked ? "Tap to see changes." : "";
        if (isDvirLogRemoved) {
            return {
                id: BannerId.LogUpdate,
                type: "warning",
                title: TopbarTranslationsDictionary.LogChangeRemoved,
                content: TopbarTranslationsDictionary.LogChangeRemovedContent,
                actionText: TopbarTranslationsDictionary.LogChangeAction,
                action: isUnlocked ? openDvirPage : undefined
            };
        }

        return {
            id: BannerId.LogUpdate,
            type: "warning",
            title: TopbarTranslationsDictionary.LogChangeUpdated,
            content: TopbarTranslationsDictionary.LogChangeUpdatedContent,
            actionText: TopbarTranslationsDictionary.LogChangeAction,
            action: isUnlocked ? openDvirPage : undefined
        };
    }
    return undefined;
};

export const getWorkdayMessage = (store: DriveStore, securityService: SecurityService, currentUserId: string, startWorkday: () => void): BannerDetail | undefined => {
    let appState = store.getState();
    let isUnlocked = !getPageState(store).isLocked;

    if (!securityService.can("viewDriverClockInOut")) {
        return undefined;
    }

    if (isUnlocked && appState.data.workday[currentUserId] === WorkdayType.Stopped) {
        return {
            id: BannerId.WorkdayMessage,
            type: "warning",
            title: TopbarTranslationsDictionary.WorkdayTitle,
            content: TopbarTranslationsDictionary.WorkdayContent,
            actionText: TopbarTranslationsDictionary.WorkdayAction,
            action: startWorkday
        };
    }

    return undefined;
};

const isLockScreenCleared = (store: DriveStore) => {
    let deviceMovement = store.getState().deviceMovement;
    return deviceMovement.stateUpdate?.source === "USER_EVENT";
};

const isUserInAutomaticDriveStatus = (dutyStatusLogService: DutyStatusLogService, currentUserId: string) => {
    let dutyStatusLogs = dutyStatusLogService.getSortedHosLogs(dutyStatusLogService.getHOSLogs(false, currentUserId).filter(hosLog => hosLog.isDashboardLog()));
    let lastLog = dutyStatusLogService.getLatestLog(dutyStatusLogs);
    return lastLog && isAutomaticLog(lastLog) && (lastLog?.status === "D" || lastLog?.status === "INT_D");
};

export const getDrivingStatusMessage = (
    store: DriveStore,
    dutyStatusLogService: DutyStatusLogService,
    currentUserId: string,
    previousDriving: boolean | undefined,
    setPreviousDriving: React.Dispatch<React.SetStateAction<boolean | undefined>>,
    openHosStatusPage: () => void
): BannerDetail | undefined => {
    const currentDriving = store.getState().data.driving;

    if (previousDriving !== currentDriving) {
        if (!currentDriving && !isLockScreenCleared(store)) {
            if (isUserInAutomaticDriveStatus(dutyStatusLogService, currentUserId)) {
                let isUnlocked = !getPageState(store).isLocked;
                // let tapChangesMessage = isUnlocked ? "Tap to see changes." : "";
                return {
                    id: BannerId.DrivingStatus,
                    type: "warning",
                    title: TopbarTranslationsDictionary.StoppedDrivingTitle,
                    content: TopbarTranslationsDictionary.StoppedDrivingContent,
                    actionText: TopbarTranslationsDictionary.StoppedDrivingAction,
                    action: isUnlocked ? openHosStatusPage : undefined
                };
            }
        } else {
            setPreviousDriving(currentDriving);
        }
    }

    return undefined;
};